if (!customElements.get('de-product-loop')) {
  customElements.define('de-product-loop', class extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.de-loop__viewport');
      this.track = this.querySelector('.de-loop__track');
      this.original = this.querySelector('[data-original]');
      if (!this.original.querySelector('[data-product-id]')) return;

      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.staticMode = this.closest('.de-loop').dataset.static === 'true';
      this.paused = this.reducedMotion || this.staticMode;
      this.hover = false;
      this.focused = false;
      // Por defecto el movimiento lo lleva una animación CSS (hilo de composición,
      // inmune a cualquier jank del hilo principal). Solo pasamos a control manual
      // por JS cuando el usuario usa los botones ← →, que necesitan un salto puntual
      // que una animación CSS en bucle no puede expresar.
      this.manual = false;
      this.position = 0;
      this.speed = Number(this.closest('.de-loop').dataset.speed) || 30;

      this.pauseButton = this.querySelector('[data-pause]');
      if (this.pauseButton) {
        this.updatePauseButton();
        this.pauseButton.addEventListener('click', () => {
          this.paused = !this.paused;
          this.updatePauseButton();
          this.syncPausedClass();
        });
      }
      this.querySelector('[data-next]').addEventListener('click', () => this.moveBy(1));
      this.querySelector('[data-previous]').addEventListener('click', () => this.moveBy(-1));
      this.viewport.addEventListener('pointerenter', () => (this.hover = true));
      this.viewport.addEventListener('pointerleave', () => (this.hover = false));
      this.viewport.addEventListener('focusin', () => (this.focused = true));
      this.viewport.addEventListener('focusout', () => (this.focused = this.viewport.contains(document.activeElement)));
      this.viewport.addEventListener('pointerdown', () => {
        this.paused = true;
        this.updatePauseButton();
        this.syncPausedClass();
      });

      new ResizeObserver(() => {
        const newDistance = this.original.getBoundingClientRect().width;
        if (this.manual && this.distance && newDistance) {
          this.position = (this.position / this.distance) * newDistance;
        }
        this.distance = newDistance;
        if (newDistance) {
          // Usamos px exactos (no %) porque el track tiene su propio padding
          // horizontal: un translate de -50% incluiría ese padding y desfasaría
          // el punto donde debe encajar el ciclo del loop.
          this.track.style.setProperty('--de-loop-duration', newDistance / this.speed + 's');
          this.track.style.setProperty('--de-loop-distance', -newDistance + 'px');
          this.maybeEnableAuto();
        }
      }).observe(this.original);

      // Mientras la página termina de cargar, otras secciones (hero, video) pueden
      // seguir acomodando su alto y empujar este carrusel fuera y dentro del
      // viewport por un instante. Eso es un "no visible" real para el
      // IntersectionObserver, así que se pausaría de forma correcta pero se nota
      // como un frenón. Ignoramos esa señal hasta que la página esté asentada.
      this.pageSettled = document.readyState === 'complete';
      if (!this.pageSettled) {
        window.addEventListener(
          'load',
          () => {
            this.pageSettled = true;
            this.syncPausedClass();
          },
          { once: true }
        );
      }
      new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          clearTimeout(this.hideTimer);
          this.visible = true;
          this.syncPausedClass();
        } else if (this.pageSettled) {
          this.hideTimer = setTimeout(() => {
            this.visible = false;
            this.syncPausedClass();
          }, 300);
        }
      }).observe(this);

      this.tick = this.tick.bind(this);

      // Clonar la pista y priorizar imágenes es trabajo pesado; hacerlo de inmediato
      // compite por el hilo principal con la carga inicial de la página. Lo aplazamos
      // a un momento en que el navegador tenga un respiro.
      const schedule = window.requestIdleCallback || ((callback) => setTimeout(callback, 200));
      schedule(() => {
        // El navegador no detecta con anticipación las imágenes dentro de una pista
        // animada. Priorizamos las primeras tarjetas para que no aparezcan vacías
        // mientras el carrusel avanza.
        [...this.original.querySelectorAll('img[loading="lazy"]')].slice(0, 12).forEach((image) => {
          image.loading = 'eager';
          image.fetchPriority = 'high';
        });
        this.clone = this.original.cloneNode(true);
        this.clone.dataset.clone = '';
        this.clone.setAttribute('aria-hidden', 'true');
        this.clone.inert = true;
        this.clone.querySelectorAll('a, button, input, select, textarea').forEach((element) => (element.tabIndex = -1));
        this.original.after(this.clone);
        this.cloneReady = true;
        this.maybeEnableAuto();
      }, { timeout: 1500 });
    }
    syncPausedClass() {
      this.track.classList.toggle('is-paused', this.paused || this.visible === false);
    }
    maybeEnableAuto() {
      // Solo activamos la animación CSS cuando YA tenemos el clon insertado y el
      // ancho real medido (--de-loop-distance). Si arranca antes, corre sin saber
      // cuánto avanzar y luego brinca en cuanto llega el dato: ese es el glitch
      // chiquito del inicio.
      if (this.reducedMotion || this.staticMode || this.manual || !this.cloneReady || !this.distance) return;
      if (!this.track.classList.contains('is-auto')) {
        this.track.classList.add('is-auto');
      }
      this.syncPausedClass();
    }
    enterManualMode() {
      if (this.manual) return;
      this.manual = true;
      // Convertimos el punto exacto donde iba la animación CSS en la posición
      // manual de arranque, para que el salto de los botones no brinque de golpe.
      const computedTransform = getComputedStyle(this.track).transform;
      const matrix = computedTransform === 'none' ? new DOMMatrixReadOnly() : new DOMMatrixReadOnly(computedTransform);
      this.position = -matrix.m41;
      this.track.classList.remove('is-auto');
      this.last = performance.now();
      requestAnimationFrame(this.tick);
    }
    tick(now) {
      const elapsed = Math.min(now - this.last, 64) / 1000;
      this.last = now;
      if (!this.paused && !this.hover && !this.focused && this.visible && !document.hidden && this.distance) {
        this.position = (this.position + this.speed * elapsed) % this.distance;
        this.render();
      }
      this.frame = requestAnimationFrame(this.tick);
    }
    moveBy(direction) {
      this.enterManualMode();
      this.paused = true;
      this.updatePauseButton();
      const card = this.original.querySelector('[data-product-id]');
      const amount = card ? card.offsetWidth + parseFloat(getComputedStyle(this.original).gap) : 250;
      this.position = (this.position + direction * amount) % this.distance;
      if (this.position < 0) this.position += this.distance;
      this.render();
    }
    render() {
      this.track.style.transform = `translate3d(${-this.position}px, 0, 0)`;
    }
    updatePauseButton() {
      this.pauseButton.textContent = this.paused ? 'Reanudar' : 'Pausar';
      this.pauseButton.setAttribute('aria-pressed', String(this.paused));
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.hideTimer);
    }
  });
}
